/**
 * O PAINEL SE ATUALIZA SOZINHO — sem ninguém precisar saber o que é Ctrl+Shift+R.
 *
 * ── O DEFEITO ─────────────────────────────────────────────────────────────
 *
 * Uma aba aberta antes do deploy continua rodando o código antigo até alguém
 * recarregar À MÃO. Não há tela branca e não há erro: os assets velhos ficam em
 * disco de propósito (ver `deploy.sh`), então tudo continua funcionando — com o
 * produto de ontem. A pessoa vê o defeito que já foi corrigido, relata de novo,
 * e a única cura era uma tecla que ela não tem por que conhecer.
 *
 * Isso é diferente do caso que `lazy-page.ts` e `PageFailed.vue` já cobrem:
 * aqueles agem quando a aba JÁ QUEBROU — um chunk que sumiu, um import que
 * falha. É rede de segurança, e chega tarde por definição. Aqui a pergunta é
 * outra: como a aba descobre que há versão nova enquanto ainda está inteira.
 *
 * ── O DESENHO ─────────────────────────────────────────────────────────────
 *
 * O build carimba `dist/version.json` com o nome do bundle de entrada (ver
 * `stampVersion` em `vite.config.ts`). Esta aba pergunta por ele no boot, ao
 * voltar a ficar visível, e quando o `queue/stream` do shell diz que é hora; a
 * PRIMEIRA resposta é comparada ao carimbo do HTML carregado, e qualquer resposta
 * diferente depois disso significa deploy.
 *
 * ── SEM RELÓGIO (29/09) ───────────────────────────────────────────────────
 *
 * Era um `setInterval` de 60 s — o último relógio permanente do painel, contra a
 * doutrina de zero polling (27/08). Agora quem publica AVISA: o `deploy.sh` bate
 * em `POST /v3/internal/deployed` e o back manda `pulse deploy` para todo stream
 * de tenant aberto (`use-queue-alert.ts`). O evento só ACORDA a pergunta; a
 * resposta continua a ser o `version.json`.
 *
 * O aviso pode não chegar — back fora do ar durante o deploy do front, curl que
 * falhou. Por isso o stream também pergunta ao (RE)CONECTAR: o back que volta
 * derruba e reabre todos os streams, e cada aba confere nesse instante. Página
 * sem sessão (sign-in) não tem stream e fica com o boot e o `visibilitychange`.
 *
 * A partir daí não se recarrega NADA — só se levanta a bandeira. Quem age é a
 * guarda do router, na próxima navegação: o clique no menu vira carregamento de
 * página inteira em vez de troca de rota. É o momento certo e é o único momento
 * seguro. Recarregar no instante em que se descobre jogaria fora uma mensagem
 * meio escrita no inbox, um formulário meio preenchido, um filtro montado — sem
 * a pessoa ver por quê e sem poder desfazer. Trocar de tela já descarta esse
 * estado; a recarga entra escondida atrás de um gesto que a pessoa fez.
 *
 * ── AS TRÊS PORTAS QUE FICAM FECHADAS ─────────────────────────────────────
 *
 * 1. `import.meta.env.DEV` — em desenvolvimento o HMR já faz isto, e melhor.
 *
 * 2. Dentro de um iframe. O painel nunca é embutido; o que é embutido é o chat
 *    do site publicado do tenant, que roda ESTE mesmo `main.ts` em origem opaca
 *    (ver `storage-shim.ts`). Lá o `fetch` relativo seria cross-origin e falharia
 *    em silêncio — e mesmo que passasse, recarregar um chat no meio de uma
 *    conversa de suporte é exatamente o que não se quer.
 *
 * 3. Depois de a bandeira subir. A resposta não vai mudar de novo para pior, e
 *    continuar perguntando é gastar rede para reconfirmar o que já se sabe.
 */

/** A versão que ESTA aba está rodando. Vazio até a primeira resposta. */
let running = "";

/** Há versão nova no servidor, e a próxima navegação deve ser recarga. */
let pending = false;

/**
 * O carimbo que o servidor está publicando agora, ou `null` se a pergunta não
 * pôde ser feita.
 *
 * `null` cobre rede caída, servidor a meio de um deploy, e resposta que não é
 * JSON — todos com o mesmo desfecho: não se sabe, então não se conclui nada. O
 * silêncio aqui é a resposta certa; a alternativa seria uma aba que se recarrega
 * porque o wifi piscou.
 *
 * `no-store` mais `?v=` no endereço: o cabeçalho manda no navegador, o endereço
 * manda em qualquer cache no meio do caminho que o ignore. Sem os dois, a aba
 * pergunta durante horas e recebe de volta o mesmo carimbo guardado no boot.
 */
async function readPublishedVersion(): Promise<string | null> {
	try {
		const res = await fetch(`/version.json?v=${Date.now()}`, { cache: "no-store" });
		if (!res.ok) return null;
		const body: unknown = await res.json();
		const version = (body as { version?: unknown })?.version;
		return typeof version === "string" && version ? version : null;
	} catch {
		return null;
	}
}

/**
 * Uma pergunta e sua conclusão.
 *
 * Exportada porque é a porta dos gatilhos: o boot e o `visibilitychange` aqui,
 * o `pulse deploy` e a reconexão em `use-queue-alert.ts`. Chamar demais é
 * barato — depois da bandeira ela nem pergunta.
 */
export async function checkForNewDeploy(): Promise<void> {
	if (pending) return;
	const version = await readPublishedVersion();
	if (!version) return;
	if (!running) {
		const loaded =
			typeof document === "undefined"
				? null
				: document.querySelector('meta[name="benvox-version"]')?.getAttribute("content");
		running = loaded || version;
	}
	if (version !== running) pending = true;
}

/** Esta aba está embutida em outra página? */
function embedded(): boolean {
	try {
		return window.top !== window.self;
	} catch {
		// Em origem opaca a comparação pode estourar. Se estourou, é iframe.
		return true;
	}
}

/**
 * Começa a vigiar. Chamado uma vez, no boot.
 */
export function watchForNewDeploy(): void {
	if (import.meta.env.DEV || embedded()) return;

	void checkForNewDeploy();

	/**
	 * E ao voltar para a aba.
	 *
	 * O Chrome chega a congelar abas ociosas por completo, e uma aba congelada
	 * não lê o stream: o aviso do deploy passa por ela sem ser ouvido. Revalidar
	 * ao reaparecer fecha essa janela no momento exato em que ela importa: a
	 * pessoa acabou de voltar e o próximo clique já é a recarga.
	 */
	document.addEventListener("visibilitychange", () => {
		if (!document.hidden) void checkForNewDeploy();
	});
}

/** Se a próxima navegação deve ser um carregamento de página inteira. */
export function newDeployIsWaiting(): boolean {
	return pending;
}

/** Só para os testes: devolve o módulo ao estado de quem nunca perguntou nada. */
export function resetDeployWatchForTests(): void {
	running = "";
	pending = false;
}
